<template>
    <div class="modal-card attachments-modal">
        <div id="otimizar_imagem" class="is-hidden" />

        <header class="modal-card-head attachments-header">
            <div class="attachments-heading">
                <span class="attachments-heading-icon">
                    <b-icon icon="paperclip" size="is-medium" />
                </span>
                <div>
                    <p class="modal-card-title">{{ titleName || 'Anexos' }}</p>
                    <p class="attachments-subtitle">{{ resumoAnexos }}</p>
                </div>
            </div>
        </header>

        <section class="modal-card-body attachments-body">
            <div class="columns is-variable is-5">
                <div class="column is-5">
                    <b-field class="upload-field">
                        <b-upload
                            v-model="fileType"
                            class="upload-control"
                            accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.wmv"
                            multiple
                            drag-drop
                            expanded
                            :disabled="enviando"
                            @input="incluirAnexo"
                        >
                            <section class="upload-area" :class="{ 'is-uploading': enviando }">
                                <span class="upload-icon">
                                    <b-icon :icon="enviando ? 'cloud-upload' : 'cloud-upload-outline'" size="is-large" />
                                </span>
                                <strong>{{ enviando ? 'Enviando arquivos…' : 'Adicionar arquivos' }}</strong>
                                <span class="upload-instruction">Arraste aqui ou clique para selecionar</span>
                                <span class="upload-formats">PDF, imagens e vídeos</span>
                            </section>
                        </b-upload>
                    </b-field>

                    <div v-if="enviando" class="upload-progress">
                        <b-progress type="is-primary" size="is-small" />
                        <span>Otimizando e enviando {{ quantidadeSelecionada }}…</span>
                    </div>

                    <div class="upload-tip">
                        <b-icon icon="information-outline" size="is-small" />
                        <span>Você pode selecionar vários arquivos de uma só vez.</span>
                    </div>
                </div>

                <div class="column is-7 attachments-list-column">
                    <div class="attachments-list-header">
                        <div>
                            <strong>Arquivos anexados</strong>
                            <span v-if="anexos.length" class="tag is-light">{{ anexos.length }}</span>
                        </div>
                        <b-button size="is-small" type="is-light" icon-left="refresh" :loading="carregando" title="Atualizar" @click="loadAnexos" />
                    </div>

                    <div class="attachments-list">
                        <b-loading :active="carregando" :is-full-page="false" />

                        <div v-if="!carregando && anexos.length === 0" class="attachments-empty">
                            <span class="empty-icon"><b-icon icon="file-outline" size="is-large" /></span>
                            <strong>Nenhum arquivo anexado</strong>
                            <span>Os documentos enviados aparecerão aqui.</span>
                        </div>

                        <template v-else>
                            <article v-for="(anexo, index) in anexos" :key="anexo.id || `${nomeArquivo(anexo)}-${index}`" class="attachment-card">
                                <button
                                    class="attachment-main"
                                    type="button"
                                    :disabled="!anexo.id"
                                    :title="anexo.id ? 'Abrir arquivo' : nomeArquivo(anexo)"
                                    @click="anexo.id && visualizarAnexo(anexo.id)"
                                >
                                    <span class="attachment-icon" :class="`is-${tipoArquivo(anexo)}`">
                                        <b-icon :icon="iconeArquivo(anexo)" />
                                    </span>
                                    <span class="attachment-info">
                                        <strong>{{ nomeArquivo(anexo) }}</strong>
                                        <small>
                                            {{ extensaoArquivo(anexo) }}
                                            <template v-if="anexo.name"> · {{ anexo.name }}</template>
                                        </small>
                                    </span>
                                </button>

                                <div v-if="anexo.id" class="attachment-actions">
                                    <b-tooltip v-if="ehPdf(anexo) && showButtonExibirLaudo" position="is-left" label="Exibir no laudo">
                                        <b-switch
                                            v-model="anexo.exibirNoLaudo"
                                            size="is-small"
                                            :disabled="alterandoLaudoId === anexo.id"
                                            @input="exibeNoLaudo(anexo)"
                                        />
                                    </b-tooltip>
                                    <b-button
                                        type="is-light"
                                        size="is-small"
                                        icon-left="open-in-new"
                                        title="Abrir arquivo"
                                        @click="visualizarAnexo(anexo.id)"
                                    />
                                    <b-button
                                        type="is-danger is-light"
                                        size="is-small"
                                        icon-left="delete-outline"
                                        :loading="removendoId === anexo.id"
                                        :title="$t('ANEXOS.REMOVERANEXO')"
                                        @click="removerAnexo(anexo)"
                                    />
                                </div>
                            </article>
                        </template>
                    </div>
                </div>
            </div>
        </section>

        <footer class="modal-card-foot attachments-footer">
            <span class="has-text-grey is-size-7">{{ resumoAnexos }}</span>
            <b-button @click="$emit('close')">Fechar</b-button>
        </footer>
    </div>
</template>

<script>
import http from '@/http'
import { mapState } from 'vuex'
import utilFile from '../assets/js/views/atendimento/utilFile.js'

export default {
    name: 'ModalAnexosGenerico',
    props: {
        id: Number,
        guiaId: Number,
        titleName: String,
        controller: { type: Object, required: true },
        tipoParam: { type: Object, required: true },
        aliasName: String,
    },
    data() {
        return {
            anexos: [],
            fileType: [],
            carregando: false,
            enviando: false,
            quantidadeSelecionada: 0,
            removendoId: null,
            alterandoLaudoId: null,
            showButtonExibirLaudo: false,
        }
    },
    computed: {
        ...mapState(['config', 'unidadeId']),
        resumoAnexos() {
            if (this.carregando) return 'Carregando arquivos…'
            if (this.anexos.length === 0) return 'Nenhum arquivo anexado'
            return `${this.anexos.length} ${this.anexos.length === 1 ? 'arquivo anexado' : 'arquivos anexados'}`
        },
    },
    created() {
        this.loadAnexos()
    },
    methods: {
        async exibeNoLaudo(item) {
            this.alterandoLaudoId = item.id
            try {
                await http.post(`/api/${this.controller.mainController}/anexos`, {
                    id: item.id,
                    exibirNoLaudo: item.exibirNoLaudo,
                })
            } catch (error) {
                item.exibirNoLaudo = !item.exibirNoLaudo
                this.mostrarErro(error, 'Não foi possível alterar a exibição no laudo.')
            } finally {
                this.alterandoLaudoId = null
            }
        },
        async loadAnexos() {
            this.carregando = true
            try {
                const params = new URLSearchParams({
                    [this.tipoParam.firstType]: this.id,
                    aliasName: this.aliasName || '',
                })
                const response = await http.get(`/api/${this.controller.mainController}/anexos?${params}`)
                this.anexos = Array.isArray(response.data) ? response.data : []
                this.showButtonExibirLaudo = this.tipoParam.firstType === 'agendaId'
            } catch (error) {
                this.anexos = []
                this.mostrarErro(error, 'Não foi possível carregar os anexos.')
            } finally {
                this.carregando = false
            }
        },
        visualizarAnexo(anexoId) {
            const params = new URLSearchParams({ id: this.id, anexoId, unidadeId: this.unidadeId })
            window.open(`/api/${this.controller.mainController}/anexoPdf?${params}`, '_blank', 'noopener')
        },
        removerAnexo(anexo) {
            const nome = this.nomeArquivo(anexo)
            this.$buefy.dialog.confirm({
                title: this.$t('ANEXOS.EXCLUIRANEXO'),
                message: `Confirma a exclusão de <strong>${this.escaparHtml(nome)}</strong>?`,
                confirmText: this.$t('SISTEMA.CONFIRMAR'),
                cancelText: this.$t('SISTEMA.CANCELAR'),
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                    this.removendoId = anexo.id
                    try {
                        const payload = { tipo: this.tipoParam.firstType, anexoId: anexo.id }
                        const response = await http.post(
                            `/api/${this.controller.mainController}/${this.controller.methodRemoverController}?anexoId=${anexo.id}`,
                            payload
                        )
                        if (response.body?.erro) throw response
                        this.anexos = this.anexos.filter((item) => item.id !== anexo.id)
                        this.$buefy.toast.open({ message: 'Anexo removido.', type: 'is-success' })
                    } catch (error) {
                        this.mostrarErro(error, 'Não foi possível remover o anexo.')
                    } finally {
                        this.removendoId = null
                    }
                },
            })
        },
        async incluirAnexo(fileList) {
            const arquivos = Array.isArray(fileList) ? fileList : fileList ? [fileList] : []
            if (arquivos.length === 0 || this.enviando) return

            this.enviando = true
            this.quantidadeSelecionada = arquivos.length
            try {
                const formData = new FormData()
                const setType = this.tipoParam.firstType === 'agendaId' ? this.tipoParam.firstType : this.tipoParam.secondType

                formData.append(this.tipoParam.firstType, this.id)
                formData.append('type', setType)
                if (this.guiaId > 0) formData.append(this.tipoParam.secondType, this.guiaId)

                for (const file of arquivos) {
                    if (utilFile.isFileImage(file)) {
                        const newBlob = await utilFile.createFileReader(file)
                        const percentual = parseFloat(this.config.percentualOtimizacaoImagem) || 0.8
                        const newFile = await utilFile.resizeImage(newBlob, file.type, file.name, percentual)
                        formData.append('anexo', newFile, newFile.name)
                    } else {
                        formData.append('anexo', file, file.name)
                    }
                }

                const response = await http.post(`/api/${this.controller.mainController}/${this.controller.methodAnexoController}`, formData)
                this.$buefy.toast.open({ message: response.data?.sucesso || 'Arquivos anexados com sucesso.', type: 'is-success' })
                this.fileType = []
                await this.loadAnexos()
            } catch (error) {
                this.mostrarErro(error, 'Não foi possível enviar os arquivos.')
            } finally {
                this.enviando = false
                this.quantidadeSelecionada = 0
            }
        },
        nomeArquivo(anexo) {
            return anexo.fileName || this.nomeDoCaminho(anexo.anexo) || anexo.name || 'Arquivo sem nome'
        },
        nomeDoCaminho(caminho) {
            return caminho ? caminho.replace(/^.*[\\/]/, '') : ''
        },
        extensaoArquivo(anexo) {
            const extensao = this.nomeArquivo(anexo).split('.').pop()
            return extensao && extensao !== this.nomeArquivo(anexo) ? extensao.toUpperCase() : 'ARQUIVO'
        },
        ehPdf(anexo) {
            return this.extensaoArquivo(anexo) === 'PDF'
        },
        tipoArquivo(anexo) {
            const extensao = this.extensaoArquivo(anexo).toLowerCase()
            if (extensao === 'pdf') return 'pdf'
            if (['jpg', 'jpeg', 'png'].includes(extensao)) return 'image'
            if (['mp4', 'mov', 'wmv'].includes(extensao)) return 'video'
            return 'file'
        },
        iconeArquivo(anexo) {
            const tipo = this.tipoArquivo(anexo)
            if (tipo === 'pdf') return 'file-pdf-box'
            if (tipo === 'image') return 'file-image-outline'
            if (tipo === 'video') return 'file-video-outline'
            return 'file-outline'
        },
        escaparHtml(valor) {
            return String(valor).replace(/[&<>'"]/g, (caractere) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[caractere]))
        },
        mostrarErro(error, mensagemPadrao) {
            const mensagem = error?.data?.erro || error?.body?.erro || error?.message || mensagemPadrao
            this.$buefy.toast.open({ duration: 5000, message: mensagem, type: 'is-danger' })
        },
    },
}
</script>

<style scoped>
.attachments-modal {
    width: min(920px, calc(100vw - 2rem));
    max-height: min(720px, calc(100vh - 2rem));
    border-radius: 14px;
    overflow: hidden;
}
.attachments-header {
    padding: 1rem 1.25rem;
    background: #fff;
    border-bottom: 1px solid #e8edf3;
}
.attachments-heading {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
}
.attachments-heading-icon,
.upload-icon,
.empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #3273dc;
    background: #edf5ff;
}
.attachments-heading-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
}
.attachments-subtitle {
    margin-top: 0.15rem;
    color: #7a8796;
    font-size: 0.78rem;
}
.attachments-body {
    padding: 1.25rem;
    overflow: hidden auto;
    background: #f7f9fc;
}
.upload-field,
.upload-control {
    width: 100%;
}
.upload-area {
    min-height: 230px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border: 2px dashed #bfd0e3;
    border-radius: 14px;
    background: #fff;
    color: #34495e;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}
.upload-area:hover {
    border-color: #3273dc;
    background: #fafdff;
    transform: translateY(-1px);
}
.upload-area.is-uploading {
    pointer-events: none;
    opacity: 0.75;
}
.upload-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 0.35rem;
    border-radius: 50%;
}
.upload-instruction {
    color: #66788a;
    text-align: center;
}
.upload-formats {
    margin-top: 0.3rem;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    background: #f0f3f7;
    color: #7b8794;
    font-size: 0.72rem;
}
.upload-progress {
    margin-top: 0.75rem;
    color: #5f6f80;
    font-size: 0.78rem;
    text-align: center;
}
.upload-tip {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.85rem;
    color: #7a8796;
    font-size: 0.76rem;
}
.attachments-list-column {
    display: flex;
    flex-direction: column;
    min-height: 330px;
}
.attachments-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}
.attachments-list-header > div {
    display: flex;
    align-items: center;
    gap: 0.45rem;
}
.attachments-list {
    position: relative;
    display: grid;
    align-content: start;
    gap: 0.55rem;
    min-height: 270px;
    max-height: 410px;
    overflow-y: auto;
    padding-right: 0.2rem;
}
.attachments-empty {
    min-height: 270px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    border: 1px dashed #d8e0e9;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.7);
    color: #7a8796;
    text-align: center;
}
.attachments-empty strong {
    color: #435466;
}
.empty-icon {
    width: 58px;
    height: 58px;
    margin-bottom: 0.3rem;
    border-radius: 50%;
}
.attachment-card {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
    padding: 0.55rem;
    border: 1px solid #e2e8ef;
    border-radius: 12px;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.attachment-card:hover {
    border-color: #c6d5e5;
    box-shadow: 0 5px 16px rgba(31, 53, 78, 0.06);
}
.attachment-main {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
    flex: 1;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
}
.attachment-main:disabled {
    cursor: default;
}
.attachment-icon {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: #526577;
    background: #f0f3f6;
}
.attachment-icon.is-pdf {
    color: #c0392b;
    background: #fff0ef;
}
.attachment-icon.is-image {
    color: #2574a9;
    background: #edf7ff;
}
.attachment-icon.is-video {
    color: #7d4cdb;
    background: #f4efff;
}
.attachment-info {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
}
.attachment-info strong,
.attachment-info small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.attachment-info strong {
    color: #34495e;
}
.attachment-info small {
    margin-top: 0.12rem;
    color: #8a97a5;
    font-size: 0.7rem;
}
.attachment-actions {
    display: flex;
    align-items: center;
    gap: 0.3rem;
}
.attachments-footer {
    display: flex;
    justify-content: space-between;
    padding: 0.8rem 1.25rem;
    border-top: 1px solid #e8edf3;
    background: #fff;
}
@media (max-width: 768px) {
    .attachments-modal {
        width: calc(100vw - 1rem);
        max-height: calc(100vh - 1rem);
    }
    .attachments-body {
        padding: 1rem;
    }
    .upload-area {
        min-height: 180px;
    }
    .attachments-list {
        max-height: none;
    }
    .attachment-actions {
        flex-wrap: wrap;
        justify-content: flex-end;
    }
    .attachments-footer > span {
        display: none;
    }
    .attachments-footer .button {
        width: 100%;
    }
}
</style>
